import { Link } from "react-router-dom";
import { companyDetails, images, academyProgram, benefits, testimonials } from "../data";
import { getOptimizedImageUrl } from "../lib/utils";
import SEO from "../components/SEO";

export default function Home() {
  const homeSchema = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "WebSite",
        "@id": "https://lederskab.nu/#website",
        "url": "https://lederskab.nu/",
        "name": "Lederskab.nu",
        "description": "Det landsdækkende lederakademi",
        "inLanguage": "da-DK"
      },
      {
        "@type": "EducationalOrganization",
        "@id": "https://lederskab.nu/#organization",
        "name": "Lederskab.nu",
        "url": "https://lederskab.nu/",
        "logo": "https://assets.auxodev.dk/cdn-cgi/image/width=500,format=auto,quality=90/assets/22d442ab-45d9-4214-861d-1df67f744e88/lederskab-nu-logoer-2026-2.png",
        "telephone": "+4553876106",
        "email": "anita@stressfrikommunikation.dk",
        "address": {
          "@type": "PostalAddress",
          "streetAddress": "Parkvej 25 3. th.",
          "addressLocality": "Vejle",
          "postalCode": "7100",
          "addressCountry": "DK"
        }
      }
    ]
  };

  return (
    <div className="bg-white">
      <SEO
        title="Lederskab.nu | Tag ansvar for eget lederskab – Lederakademi"
        description="Lederskab.nu tilbyder et 12-måneders lederforløb i hele Danmark med fokus på personligt ansvar, stærkere kommunikation og bæredygtig ledelse. Se kommende hold her."
        canonicalPath="/"
        structuredData={homeSchema}
      />
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-maersk-light via-white to-white pt-16 pb-24 md:py-28 overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            {/* Left Content */}
            <div className="lg:col-span-7 space-y-6 text-left">
              <div className="inline-flex items-center gap-2 bg-brand-orange-light border border-brand-orange/20 px-3 py-1.5 rounded-full">
                <span className="w-2 h-2 rounded-full bg-brand-orange animate-pulse" />
                <span className="text-xs font-bold uppercase tracking-wider text-brand-orange">
                  Landsdækkende Lederakademi
                </span>
              </div>
              
              <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold text-maersk-dark leading-tight">
                {companyDetails.slogan}
              </h1>
              
              <p className="text-navy-700 text-lg sm:text-xl leading-relaxed max-w-2xl">
                Et 12-måneders lederforløb for ledere og ejerledere, der ønsker mere retning, stærkere relationer og bedre resultater.
              </p>

              {/* 3 Core Pillars Badges */}
              <div className="flex flex-row flex-wrap gap-2 sm:gap-3 pt-2">
                <div className="bg-white border border-maersk-blue/10 rounded-xl p-2 sm:p-3 px-3 sm:px-5 text-center shadow-xs flex flex-col justify-center min-h-[72px] sm:min-h-[80px] shrink-0">
                  <span className="block text-xl sm:text-2xl font-extrabold text-maersk-blue">4</span>
                  <span className="text-[9px] sm:text-xs font-bold uppercase tracking-wider text-gray-400">Kursusdage</span>
                </div>
                <div className="bg-white border border-maersk-blue/10 rounded-xl p-2 sm:p-3 px-3 sm:px-5 text-center shadow-xs flex flex-col justify-center min-h-[72px] sm:min-h-[80px] shrink-0">
                  <span className="block text-xl sm:text-2xl font-extrabold text-maersk-blue">4</span>
                  <span className="text-[9px] sm:text-xs font-bold uppercase tracking-wider text-gray-400">Moduler</span>
                </div>
                <div className="bg-white border border-maersk-blue/10 rounded-xl p-2 sm:p-3 px-4 sm:px-6 text-center shadow-xs flex flex-col justify-center min-h-[72px] sm:min-h-[80px] shrink-0">
                  <span className="block text-xl sm:text-2xl font-extrabold text-maersk-blue">12</span>
                  <span className="text-[9px] sm:text-xs font-bold uppercase tracking-wider text-gray-400 font-sans whitespace-nowrap">Måneders Udvikling</span>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-4 pt-4">
                <Link
                  to="/kommende-hold"
                  className="bg-maersk-blue hover:bg-maersk-hover text-white text-center px-8 py-4 rounded-md font-bold tracking-wide transition-all duration-300 shadow-md hover:shadow-lg hover:-translate-y-0.5 active:translate-y-0"
                >
                  Se kommende hold <i className="fa-solid fa-arrow-right ml-2" />
                </Link>
                <Link
                  to="/kontakt"
                  className="bg-white hover:bg-maersk-light text-maersk-dark text-center border border-maersk-blue/30 px-8 py-4 rounded-md font-bold tracking-wide transition-all duration-300 hover:border-maersk-blue"
                >
                  Book en afklarende samtale
                </Link>
              </div>
            </div>

            {/* Right Image (Hero Image - Workshop with people) */}
            <div className="lg:col-span-5 relative">
              <div className="absolute -inset-4 bg-gradient-to-tr from-maersk-blue/20 to-brand-orange/10 rounded-2xl blur-xl opacity-70" />
              <div className="relative bg-white overflow-hidden rounded-2xl shadow-2xl border border-maersk-light">
                <img
                  src={getOptimizedImageUrl(images.workshopHero, "width=600,quality=85,format=auto")}
                  alt="Deltagere i lederworkshop hos Lederskab.nu"
                  width="600"
                  height="500"
                  fetchPriority="high"
                  decoding="async"
                  className="w-full h-[500px] object-cover"
                />
                <div className="absolute bottom-4 left-4 right-4 bg-white/90 backdrop-blur-xs p-4 rounded-xl border border-white/20 shadow-lg text-left">
                  <p className="text-xs font-bold text-brand-orange uppercase tracking-wider">Praksisnær læring</p>
                  <p className="text-xs text-maersk-dark font-medium mt-1">Vi fokuserer på transformation, træning og implementering over tid.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* "What's In It For You" Section */}
      <section className="py-20 bg-navy-50 border-y border-maersk-light">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="text-xs font-bold uppercase tracking-wider text-brand-orange bg-brand-orange-light px-3 py-1 rounded-full">
              Hvad får du ud af det?
            </span>
            <h2 className="font-display text-3xl sm:text-4xl font-bold text-maersk-dark mt-3">
              Dét får du ud af forløbet
            </h2>
            <p className="text-navy-700 mt-3 text-base sm:text-lg">
              Efter forløbet vil du som deltager have opbygget konkrete kompetencer og personlig styrke til at gøre en mærkbar forskel i hverdagen.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {benefits.map((benefit, idx) => (
              <div
                key={idx}
                className="bg-white p-8 rounded-2xl border border-maersk-blue/10 shadow-xs hover:shadow-md transition-all duration-300 text-left space-y-4"
              >
                <div className="w-12 h-12 rounded-xl bg-brand-orange-light flex items-center justify-center text-brand-orange text-xl">
                  <i className={benefit.icon} />
                </div>
                <h3 className="font-display font-bold text-maersk-dark text-lg">
                  {benefit.title}
                </h3>
                <p className="text-navy-700 text-sm leading-relaxed">
                  {benefit.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* The 12-Month Leadership Program & 4 Modules */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="text-xs font-bold uppercase tracking-wider text-maersk-blue bg-maersk-light px-3 py-1 rounded-full">
              Struktureret Udviklingsforløb
            </span>
            <h2 className="font-display text-3xl sm:text-4xl font-bold text-maersk-dark mt-3">
              Lederforløbet: 4 moduler, 12 måneders transformation
            </h2>
            <p className="text-navy-700 mt-3">
              Vores forløb er bygget op, så du har tid til at implementere, reflektere og forankre værktøjerne i din egen praksis mellem modulerne.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
            {academyProgram.modules.map((mod) => (
              <div
                key={mod.number}
                className="bg-white rounded-2xl border border-maersk-blue/10 shadow-md hover:shadow-lg transition-all duration-300 flex flex-col justify-between overflow-hidden text-left"
              >
                <div className="p-6 space-y-4">
                  <div className="flex justify-between items-center">
                    <span className="text-xs font-bold uppercase tracking-wider text-brand-orange bg-brand-orange-light px-2.5 py-1 rounded-full">
                      {mod.subtitle}
                    </span>
                    <span className="text-3xl font-extrabold text-maersk-blue/20">0{mod.number}</span>
                  </div>
                  <h3 className="font-display font-bold text-maersk-dark text-lg leading-tight">
                    {mod.title}
                  </h3>
                  <p className="text-navy-700 text-xs leading-relaxed">
                    {mod.description}
                  </p>
                </div>
                <div className="p-6 bg-maersk-light border-t border-maersk-blue/5">
                  <ul className="space-y-2 text-[11px] text-navy-700 font-medium">
                    {mod.bullets.map((b, idx) => (
                      <li key={idx} className="flex items-start gap-2">
                        <i className="fa-solid fa-circle-check text-brand-orange mt-0.5 shrink-0" />
                        <span>{b}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link
              to="/kurser"
              className="inline-flex items-center gap-2 bg-maersk-blue hover:bg-maersk-hover text-white px-8 py-4 rounded-md font-bold tracking-wide transition-all duration-300 shadow-md"
            >
              Læs detaljeret beskrivelse af modulerne <i className="fa-solid fa-arrow-right text-xs" />
            </Link>
          </div>
        </div>
      </section>

      {/* Central Models & Frameworks Section (TRYG & Human Sustainability Leadership) */}
      <section className="py-20 bg-navy-900 text-white relative overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-brand-orange/10 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-maersk-blue/10 rounded-full blur-3xl pointer-events-none" />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="text-xs font-bold uppercase tracking-wider text-brand-orange bg-brand-orange/20 border border-brand-orange/30 px-3 py-1 rounded-full">
              Vores Teoretiske Fundament
            </span>
            <h2 className="font-display text-3xl sm:text-4xl font-bold text-white mt-3">
              Centrale modeller og værktøjer
            </h2>
            <p className="text-gray-300 mt-3">
              Vi underviser ikke i flyvske teorier. Vi arbejder ud fra anerkendte, praksisnære modeller, der skaber varige resultater og sunde arbejdsmiljøer.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-stretch">
            {/* Model 1: Human Sustainability Leadership */}
            <div className="bg-white/5 border border-white/10 rounded-3xl p-8 sm:p-10 text-left flex flex-col justify-between space-y-6">
              <div className="space-y-4">
                <div className="inline-flex items-center gap-2 bg-brand-orange/20 border border-brand-orange/30 px-3 py-1 rounded-full">
                  <i className="fa-solid fa-seedling text-brand-orange text-xs" />
                  <span className="text-xs font-bold uppercase tracking-wider text-brand-orange">Rammeværktøj</span>
                </div>
                <h3 className="font-display text-2xl font-bold text-white">
                  Human Sustainability Leadership
                </h3>
                <p className="text-gray-300 text-sm leading-relaxed">
                  En ledelsesfilosofi og praktisk tilgang, der sikrer, at både lederen og medarbejderne kan præstere og trives over tid. Modellen hviler på tre uundværlige søjler:
                </p>

                <div className="space-y-4 pt-2">
                  <div className="flex gap-3 items-start">
                    <div className="w-8 h-8 rounded-full bg-brand-orange/20 flex items-center justify-center text-brand-orange shrink-0 font-bold text-sm">1</div>
                    <div>
                      <h4 className="font-bold text-white text-sm">Retning</h4>
                      <p className="text-xs text-gray-400">Tydelige mål, værdier og forventningsafstemning, så alle kender vejen.</p>
                    </div>
                  </div>
                  <div className="flex gap-3 items-start">
                    <div className="w-8 h-8 rounded-full bg-brand-orange/20 flex items-center justify-center text-brand-orange shrink-0 font-bold text-sm">2</div>
                    <div>
                      <h4 className="font-bold text-white text-sm">Relationer</h4>
                      <p className="text-xs text-gray-400">Stærkt samarbejde, gensidig respekt og tillid som fundament for hverdagen.</p>
                    </div>
                  </div>
                  <div className="flex gap-3 items-start">
                    <div className="w-8 h-8 rounded-full bg-brand-orange/20 flex items-center justify-center text-brand-orange shrink-0 font-bold text-sm">3</div>
                    <div>
                      <h4 className="font-bold text-white text-sm">Rammer</h4>
                      <p className="text-xs text-gray-400">Klare strukturer, sunde forudsætninger og et arbejdsmiljø, der forebygger stress og holder i længden.</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Model 2: TRYG-modellen */}
            <div className="bg-white/5 border border-white/10 rounded-3xl p-8 sm:p-10 text-left flex flex-col justify-between space-y-6">
              <div className="space-y-4">
                <div className="inline-flex items-center gap-2 bg-maersk-blue/20 border border-maersk-blue/30 px-3 py-1 rounded-full">
                  <i className="fa-solid fa-shield-heart text-maersk-blue text-xs" />
                  <span className="text-xs font-bold uppercase tracking-wider text-maersk-blue">Psykologisk Tryghed</span>
                </div>
                <h3 className="font-display text-2xl font-bold text-white">
                  TRYG-modellen
                </h3>
                <p className="text-gray-300 text-sm leading-relaxed">
                  Vores centrale model til at opbygge og vedligeholde psykologisk tryghed og høj ydeevne i teams. TRYG står for:
                </p>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2">
                  <div className="bg-white/5 p-4 rounded-xl border border-white/5">
                    <span className="text-2xl font-extrabold text-maersk-blue block">T</span>
                    <h4 className="font-bold text-white text-xs mt-1">Troværdighed & Tydelighed</h4>
                    <p className="text-[10px] text-gray-400 mt-1">At sige det, man gør, og gøre det, man siger, med klare rammer.</p>
                  </div>
                  <div className="bg-white/5 p-4 rounded-xl border border-white/5">
                    <span className="text-2xl font-extrabold text-maersk-blue block">R</span>
                    <h4 className="font-bold text-white text-xs mt-1">Relationer & Respekt</h4>
                    <p className="text-[10px] text-gray-400 mt-1">At anerkende hinandens forskelligheder og styrke det personlige bånd.</p>
                  </div>
                  <div className="bg-white/5 p-4 rounded-xl border border-white/5">
                    <span className="text-2xl font-extrabold text-maersk-blue block">Y</span>
                    <h4 className="font-bold text-white text-xs mt-1">Ydmyghed & Ydeevne</h4>
                    <p className="text-[10px] text-gray-400 mt-1">At turde fejle, lytte aktivt og sammen skabe stærke resultater.</p>
                  </div>
                  <div className="bg-white/5 p-4 rounded-xl border border-white/5">
                    <span className="text-2xl font-extrabold text-maersk-blue block">G</span>
                    <h4 className="font-bold text-white text-xs mt-1">Gensidighed & Gennemførelse</h4>
                    <p className="text-[10px] text-gray-400 mt-1">At tage fælles ansvar og sikre, at beslutninger føres ud i livet.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Social Proof Section (Det siger lederne) */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="text-xs font-bold uppercase tracking-wider text-brand-orange bg-brand-orange-light px-3 py-1 rounded-full">
              Anbefalinger
            </span>
            <h2 className="font-display text-3xl sm:text-4xl font-bold text-maersk-dark mt-3">
              Det siger lederne
            </h2>
            <p className="text-navy-700 mt-3">
              Gennem årene har vi haft fornøjelsen af at støtte ledere, ejerledere og virksomheder i at skabe mere retning, stærkere relationer og større arbejdsglæde.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center max-w-6xl mx-auto mb-16">
            {/* Venstre kolonne: Billede */}
            <div className="lg:col-span-6">
              <div className="relative rounded-2xl overflow-hidden shadow-xl aspect-[4/3] sm:aspect-[16/10] lg:aspect-square">
                <img 
                  src="https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=85,format=auto/assets/22d442ab-45d9-4214-861d-1df67f744e88/1706599334649_1.jpg" 
                  alt="Anita Lauridsen - Strategisk partner i ledelse og arbejdsmiljø" 
                  width="800"
                  height="800"
                  loading="lazy"
                  decoding="async"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-maersk-dark/20 to-transparent pointer-events-none" />
              </div>
            </div>

            {/* Højre kolonne: Udtalelser */}
            <div className="lg:col-span-6 space-y-6">
              {/* Testimonial 1 */}
              <div className="bg-maersk-light p-8 rounded-2xl border border-maersk-blue/5 text-left flex flex-col justify-between space-y-6">
                <div>
                  <div className="flex gap-1 text-amber-400 mb-4">
                    {[...Array(5)].map((_, i) => <i key={i} className="fa-solid fa-star text-sm" />)}
                  </div>
                  <p className="text-navy-700 text-sm leading-relaxed italic">
                    "Jeg kom til Anita i en periode, hvor jeg havde mistet retning og troen på mine egne evner. Gennem forløbet fik jeg genopbygget min selvtillid, blev mere bevidst om mine værdier og fandt modet til at tage ansvar for min fremtid. I dag står jeg i et nyt lederjob med langt større ro og klarhed."
                  </p>
                </div>
                <div className="border-t border-maersk-blue/10 pt-4">
                  <h4 className="font-bold text-maersk-dark text-sm">Jette</h4>
                  <p className="text-xs text-maersk-blue font-medium mt-0.5">Souschef, Harald Nyborg</p>
                </div>
              </div>

              {/* Testimonial 2 */}
              <div className="bg-maersk-light p-8 rounded-2xl border border-maersk-blue/5 text-left flex flex-col justify-between space-y-6">
                <div>
                  <div className="flex gap-1 text-amber-400 mb-4">
                    {[...Array(5)].map((_, i) => <i key={i} className="fa-solid fa-star text-sm" />)}
                  </div>
                  <p className="text-navy-700 text-sm leading-relaxed italic">
                    "Vi oplever i dag mere struktur, tydeligere ansvar, bedre samarbejde og mindre stress i hverdagen. Anita har været med til at skabe et fælles sprog omkring ledelse, forventninger og ansvar."
                  </p>
                </div>
                <div className="border-t border-maersk-blue/10 pt-4">
                  <h4 className="font-bold text-maersk-dark text-sm">El-Grossisten A/S</h4>
                  <p className="text-xs text-maersk-blue font-medium mt-0.5">Virksomhedsforløb</p>
                </div>
              </div>
            </div>
          </div>

          {/* Checklist & Results Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 max-w-6xl mx-auto items-center pt-8 border-t border-navy-50">
            {/* Checklist */}
            <div className="lg:col-span-6 text-left space-y-6">
              <h3 className="font-display text-2xl font-bold text-maersk-dark">
                Derfor vælger ledere Lederskab.nu
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="flex items-center gap-2.5 text-sm text-navy-700 font-medium">
                  <i className="fa-solid fa-check text-brand-orange" />
                  <span>Mere ro og overblik</span>
                </div>
                <div className="flex items-center gap-2.5 text-sm text-navy-700 font-medium">
                  <i className="fa-solid fa-check text-brand-orange" />
                  <span>Tydeligere kommunikation</span>
                </div>
                <div className="flex items-center gap-2.5 text-sm text-navy-700 font-medium">
                  <i className="fa-solid fa-check text-brand-orange" />
                  <span>Stærkere relationer</span>
                </div>
                <div className="flex items-center gap-2.5 text-sm text-navy-700 font-medium">
                  <i className="fa-solid fa-check text-brand-orange" />
                  <span>Færre konflikter</span>
                </div>
                <div className="flex items-center gap-2.5 text-sm text-navy-700 font-medium">
                  <i className="fa-solid fa-check text-brand-orange" />
                  <span>Bedre trivsel</span>
                </div>
                <div className="flex items-center gap-2.5 text-sm text-navy-700 font-medium">
                  <i className="fa-solid fa-check text-brand-orange" />
                  <span>Højere medarbejderfastholdelse</span>
                </div>
                <div className="flex items-center gap-2.5 text-sm text-navy-700 font-medium">
                  <i className="fa-solid fa-check text-brand-orange" />
                  <span>Mere følgeskab</span>
                </div>
                <div className="flex items-center gap-2.5 text-sm text-navy-700 font-medium">
                  <i className="fa-solid fa-check text-brand-orange" />
                  <span>Et lederskab, der holder</span>
                </div>
              </div>
            </div>

            {/* Results in Practice */}
            <div className="lg:col-span-6 bg-maersk-light p-8 rounded-2xl border border-maersk-blue/5 text-left space-y-4">
              <h3 className="font-display text-xl font-bold text-maersk-dark">
                Resultater i praksis
              </h3>
              <ul className="space-y-3 text-sm text-navy-700">
                <li className="flex items-start gap-3">
                  <i className="fa-solid fa-circle-check text-maersk-blue mt-1 shrink-0" />
                  <span>20+ års erfaring med mennesker, drift og udvikling</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="fa-solid fa-circle-check text-maersk-blue mt-1 shrink-0" />
                  <span>Erfaring fra både transport, IT, projektledelse og arbejdsmiljø</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="fa-solid fa-circle-check text-maersk-blue mt-1 shrink-0" />
                  <span>Lederudvikling med fokus på implementering fremfor teori</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="fa-solid fa-circle-check text-maersk-blue mt-1 shrink-0" />
                  <span>Workshops, lederforløb og strategiske partnerskaber i virksomheder over hele landet</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Stressfri Kommunikation Partner Section */}
      <section className="py-20 bg-navy-50 border-y border-maersk-light">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gradient-to-r from-maersk-dark to-navy-900 rounded-3xl p-8 sm:p-12 lg:p-16 text-white text-left relative overflow-hidden shadow-2xl border border-navy-800">
            <div className="absolute top-0 right-0 w-96 h-96 bg-brand-orange/10 rounded-full blur-3xl pointer-events-none" />
            <div className="absolute bottom-0 left-0 w-96 h-96 bg-maersk-blue/10 rounded-full blur-3xl pointer-events-none" />
            
            <div className="relative z-10 space-y-6 max-w-5xl">
              <div className="inline-flex items-center gap-2 bg-brand-orange/20 border border-brand-orange/30 px-3 py-1 rounded-full">
                <i className="fa-solid fa-heart-pulse text-brand-orange text-xs" />
                <span className="text-xs font-bold uppercase tracking-wider text-brand-orange">
                  Strategisk Samarbejde
                </span>
              </div>
              
              <h2 className="font-display text-3xl sm:text-4xl font-bold text-white leading-tight">
                Sunde arbejdsmiljøer med Stressfri Kommunikation
              </h2>
              
              <p className="text-gray-300 text-base sm:text-lg leading-relaxed">
                Lederskab og Stressfri Kommunikation hænger uløseligt sammen. For at kunne lede andre effektivt, må man først lære at kommunikere uden at skabe unødigt pres og stress. Gennem StressFRI Kommunikation hjælper jeg samtidig virksomheder med at skabe tydeligere ledelse, stærkere samarbejde og et bæredygtigt arbejdsmiljø.
              </p>

              <div className="pt-4 flex">
                <a
                  href={companyDetails.stressfriUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-brand-orange hover:bg-brand-orange-hover text-white px-8 py-4 rounded-md font-bold tracking-wide transition-all duration-300 shadow-md hover:shadow-lg hover:-translate-y-0.5 active:translate-y-0 flex items-center gap-3 inline-flex"
                >
                  Besøg Stressfri Kommunikation
                  <i className="fa-solid fa-arrow-up-right-from-square text-sm" />
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Banner Section */}
      <section className="py-20 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center space-y-8">
          <h2 className="font-display text-3xl sm:text-4xl font-bold text-maersk-dark max-w-2xl mx-auto">
            Klar til at tage ansvar for dit lederskab?
          </h2>
          
          <p className="text-navy-700 text-lg max-w-2xl mx-auto">
            Bliv en del af næste hold på Tag ansvar for dit lederskab – ud fra dine værdier.
          </p>

          <div className="flex flex-col sm:flex-row justify-center gap-4 pt-2">
            <Link
              to="/kommende-hold"
              className="bg-brand-orange hover:bg-brand-orange-hover text-white px-8 py-4 rounded-md font-bold tracking-wide transition-all duration-300 shadow-md hover:shadow-lg hover:-translate-y-0.5 active:translate-y-0"
            >
              Se kommende hold <i className="fa-solid fa-calendar-days ml-2" />
            </Link>
            <Link
              to="/kontakt"
              className="bg-maersk-light hover:bg-maersk-blue hover:text-white text-maersk-blue px-8 py-4 rounded-md font-bold tracking-wide transition-all duration-300 flex items-center justify-center gap-2"
            >
              <i className="fa-solid fa-phone" /> Book en afklarende samtale
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
